import React from 'react';
import { ArrowRightIcon, CheckIcon, CalendarIcon, ClockIcon, UsersIcon, MonitorIcon, LayersIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function ArtOfFacilitation() {
  const {
    t
  } = useLanguage();
  const benefits = t('artOfFacilitation.benefits') as string[];
  const forWhomItems = t('artOfFacilitation.forWhomItems') as string[];
  const curriculum = t('artOfFacilitation.curriculum') as Array<{
    day: string;
    title: string;
    topics: string[];
  }>;
  return <PageTransition data-id="element-410">
      <div className="pt-12 pb-24" data-id="element-411">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-412">
          <DecorativeShape type="blob" color="bg-emerald-100/50" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-413" />
          <Animate className="max-w-3xl" data-id="element-414">
            <span className="inline-block px-4 py-2 bg-emerald-50 text-emerald-700 font-medium rounded-full text-sm mb-6" data-id="element-415">
              {t('artOfFacilitation.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight" data-id="element-416">
              {t('artOfFacilitation.title')}
              <br data-id="element-417" />
              <span className="text-emerald-600" data-id="element-418">{t('artOfFacilitation.titleHighlight')}</span>
            </h1>
            <p className="text-gray-500 text-lg mb-6 italic" data-id="element-419">{t('artOfFacilitation.audienceNote')}</p>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-420">
              {t('artOfFacilitation.aboutP1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-421">
              {t('artOfFacilitation.aboutP2')}
            </p>
            <div className="flex flex-wrap gap-3 mb-8" data-id="element-422">
              <span className="flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-423">
                <ClockIcon size={16} className="text-emerald-600" data-id="element-424" />
                {t('artOfFacilitation.duration')}
              </span>
              <span className="flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-425">
                <MonitorIcon size={16} className="text-emerald-600" data-id="element-426" />
                {t('artOfFacilitation.format')}
              </span>
              <span className="flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-427">
                <UsersIcon size={16} className="text-emerald-600" data-id="element-428" />
                {t('artOfFacilitation.audience')}
              </span>
            </div>
            <Link to="/contact" data-id="element-429">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-431" />} data-id="element-430">
                {t('artOfFacilitation.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* What you learn */}
        <section className="bg-cream py-20 mb-20" data-id="element-432">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-433">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-434">
              <Animate data-id="element-435">
                <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-436">
                  {t('artOfFacilitation.learnTitle')}
                </h2>
                <div className="space-y-3" data-id="element-437">
                  {benefits.map((benefit, i) => <div key={i} className="flex items-start gap-3" data-id="element-438">
                      <div className="mt-1 p-1 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0" data-id="element-439">
                        <CheckIcon size={14} strokeWidth={3} data-id="element-440" />
                      </div>
                      <span className="text-gray-700" data-id="element-441">{benefit}</span>
                    </div>)}
                </div>
              </Animate>
              <Animate delay={0.1} data-id="element-442">
                <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-443">
                  {t('artOfFacilitation.forWhom')}
                </h2>
                <div className="space-y-3" data-id="element-444">
                  {forWhomItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-445">
                      <div className="mt-1 p-1 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0" data-id="element-446">
                        <UsersIcon size={14} data-id="element-447" />
                      </div>
                      <span className="text-gray-700" data-id="element-448">{item}</span>
                    </div>)}
                </div>
              </Animate>
            </div>
          </div>
        </section>

        {/* Structure */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-449">
          <Animate className="mb-12" data-id="element-450">
            <h2 className="text-3xl font-bold text-charcoal" data-id="element-451">
              {t('artOfFacilitation.structureTitle')}
            </h2>
          </Animate>
          <div className="space-y-6" data-id="element-452">
            {curriculum.map((day, i) => <Animate key={i} delay={i * 0.08} data-id="element-453">
                <Card className="p-6 border-2 border-emerald-100" data-id="element-454">
                  <div className="flex items-start gap-4" data-id="element-455">
                    <div className="p-3 bg-emerald-50 rounded-xl flex-shrink-0" data-id="element-456">
                      <LayersIcon size={22} className="text-emerald-700" data-id="element-457" />
                    </div>
                    <div className="flex-1" data-id="element-458">
                      <div className="flex flex-wrap items-baseline gap-3 mb-3" data-id="element-459">
                        <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide" data-id="element-460">
                          {day.day}
                        </span>
                        <h3 className="font-bold text-charcoal text-lg" data-id="element-461">{day.title}</h3>
                      </div>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-462">
                        {day.topics.map((topic, j) => <li key={j} className="flex items-start gap-2 text-gray-600 text-sm" data-id="element-463">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" data-id="element-464" />
                            {topic}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </Card>
              </Animate>)}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-465">
          <Animate data-id="element-466">
            <Card className="p-12 bg-gradient-to-br from-emerald-50 to-teal/5 border-0" data-id="element-467">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-468">
                <CalendarIcon size={32} className="text-emerald-600" data-id="element-469" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-470">{t('artOfFacilitation.ctaTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-471">{t('artOfFacilitation.ctaDesc')}</p>
              <Link to="/contact" data-id="element-472">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-474" />} data-id="element-473">
                  {t('artOfFacilitation.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}